import { ImageEditorModal } from '@/components/modals/ImageEditorModal/ImageEditorModal';
import { useSubmitPartImageSearch } from '@/hooks/api/part-finder-image-search/useSubmitPartImageSearch';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useToast } from '@/hooks/useToast';
import { Backdrop, CircularProgress, Paper, Typography } from '@mui/material';
import { useState } from 'react';
import { convertHeicPhotos } from './convertHeicPhotos';
import { ImagePartFinderModal } from './ImagePartFinderModal';
import { isHeifFile } from './partImageFiles';
import { PartImageIntake, PartImageIntakeDialog } from './PartImageIntakeDialog';

interface HeicConversionProgress {
  photoNumber: number;
  total: number;
}

const conversionMessage = ({ photoNumber, total }: HeicConversionProgress): string =>
  total > 1 ? `Converting HEIC photo ${photoNumber} of ${total}…` : 'Converting HEIC photo…';

const unconvertedMessage = (names: string[]): string =>
  `${names.join(', ')} couldn't be converted in this browser. Export ${names.length > 1 ? 'them' : 'it'} as JPEG and try again.`;

export interface FindFromImagesProperties {
  intakeOpen: boolean;
  onIntakeOpenChange: (open: boolean) => void;
}

export const FindFromImages = ({ intakeOpen, onIntakeOpenChange }: FindFromImagesProperties) => {
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [submittedImages, setSubmittedImages] = useState<File[]>();
  const [context, setContext] = useState('');
  const [autoSplit, setAutoSplit] = useState(false);
  const [conversion, setConversion] = useState<HeicConversionProgress>();
  const { isImageSearchCroppingDisabled } = useFeatureFlags();
  const { showToast } = useToast();
  const submit = useSubmitPartImageSearch();

  const identify = (images: File[], partContext?: string, split = false) => {
    setPendingFiles([]);
    setSubmittedImages(images);
    submit.mutate({
      clientRequestId: crypto.randomUUID(),
      images,
      context: partContext?.trim() || undefined,
      autoSplit: split,
    });
  };

  const convertForBrowser = async (files: File[]): Promise<File[]> => {
    const total = files.filter(file => isHeifFile(file)).length;
    const { photos, unconverted } = await convertHeicPhotos(files, photoNumber =>
      setConversion({ photoNumber, total })
    );
    setConversion(undefined);
    if (unconverted.length > 0) {
      showToast({ message: unconvertedMessage(unconverted), type: 'warning' });
    }
    return photos;
  };

  const startWith = async ({ files, autoSplit: split, context: partContext }: PartImageIntake) => {
    onIntakeOpenChange(false);
    setSubmittedImages(undefined);
    submit.reset();
    setContext(partContext);
    setAutoSplit(split);
    const photos = await convertForBrowser(files);
    if (photos.length === 0) {
      return;
    }
    if (split || isImageSearchCroppingDisabled) {
      identify(photos, partContext, split);
    } else {
      setPendingFiles(photos);
    }
  };

  const close = () => {
    setSubmittedImages(undefined);
    submit.reset();
  };

  return (
    <>
      <PartImageIntakeDialog
        open={intakeOpen}
        onClose={() => onIntakeOpenChange(false)}
        onContinue={intake => void startWith(intake)}
      />
      <Backdrop
        open={conversion !== undefined}
        sx={{ zIndex: theme => theme.zIndex.modal + 1 }}
        data-testid='part-image-converting'
      >
        <Paper sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2, borderRadius: '12px' }}>
          <CircularProgress size={20} />
          <Typography variant='body2'>{conversion && conversionMessage(conversion)}</Typography>
        </Paper>
      </Backdrop>
      {pendingFiles.length > 0 && (
        <ImageEditorModal
          open
          files={pendingFiles}
          submitLabel='Identify'
          onComplete={editedFiles => identify(editedFiles, context, autoSplit)}
          onCancel={() => setPendingFiles([])}
        />
      )}
      {submittedImages && (
        <ImagePartFinderModal
          images={submittedImages}
          autoSplit={autoSplit}
          identificationId={submit.data?.id}
          isSubmitting={submit.isPending}
          submitError={submit.error ?? undefined}
          onClose={close}
        />
      )}
    </>
  );
};
